/**
 * Where a video upload stands, in plain words (section 5), from what this browser knows (its own
 * upload's progress) and what the backend says (the asset's status and, while unfinished, its
 * stage). Uploading 42% → Waiting for the encoder → Encoding → Ready, or Failed with the reason.
 * Pure, unit-tested.
 */

export type VideoStage = "uploading" | "waiting_for_encoder" | "encoding";
export type StateKey = "uploading" | "waiting" | "encoding" | "ready" | "failed";

export interface AssetLike {
  status: "uploading" | "ready" | "failed" | "purged";
  stage?: VideoStage;
  error?: string | null;
}

export interface UploadState {
  key: StateKey;
  label: string;
  /** One line that explains it, for under the label. */
  detail: string;
  tone: "neutral" | "warning" | "positive" | "critical";
  /** 0..1 while this browser sends the file. */
  progress?: number;
  /** Nothing more will happen by itself. */
  final: boolean;
}

/** The studio's encoder is the media agent on its Mac: the line every waiting video shows. */
export const ENCODER_LINE = "Your Mac encodes videos: it starts as soon as the ViewForm media agent is running on it.";

export function videoState(asset: AssetLike | null | undefined, local?: { progress?: number | null; error?: string | null }): UploadState {
  if (local?.error) return { key: "failed", label: "Failed", detail: plainFailure(local.error), tone: "critical", final: true };
  if (local && typeof local.progress === "number" && local.progress < 1 && (!asset || asset.status === "uploading") && (asset?.stage ?? "uploading") === "uploading") {
    const pct = Math.max(0, Math.min(99, Math.floor(local.progress * 100)));
    return { key: "uploading", label: `Uploading ${pct}%`, detail: "Keep this tab open until the upload is done.", tone: "neutral", progress: local.progress, final: false };
  }
  if (!asset) return { key: "uploading", label: "Uploading", detail: "Starting the upload.", tone: "neutral", final: false };
  switch (asset.status) {
    case "ready": return { key: "ready", label: "Ready", detail: "Encoded for every screen.", tone: "positive", final: true };
    case "failed": return { key: "failed", label: "Failed", detail: plainFailure(asset.error ?? ""), tone: "critical", final: true };
    case "purged": return { key: "failed", label: "Deleted", detail: "This video was deleted from Files.", tone: "critical", final: true };
    case "uploading":
      switch (asset.stage) {
        case "waiting_for_encoder": return { key: "waiting", label: "Waiting for the encoder", detail: ENCODER_LINE, tone: "warning", final: false };
        case "encoding": return { key: "encoding", label: "Encoding", detail: "Your Mac is making the streaming versions. This takes a few minutes.", tone: "warning", final: false };
        default: return { key: "uploading", label: "Uploading", detail: local ? "Finishing the upload." : "Being uploaded from another browser, or the upload stopped there.", tone: "neutral", final: false };
      }
  }
}

/** A failure reason the editor can act on. */
export function plainFailure(reason: string): string {
  const r = reason.toLowerCase();
  if (!r) return "The encoder could not use this file. Try exporting it again as MP4 or MOV.";
  if (r.includes("budget")) return "There is not enough storage left. Delete videos you no longer use in Files.";
  if (r.includes("invalid data") || r.includes("moov atom") || r.includes("not a valid") || r.includes("no video stream")) return "This file isn't a video the encoder can read. Export it again as MP4 or MOV.";
  if (r.includes("abort")) return "The upload was cancelled.";
  if (r.includes("failed to fetch") || r.includes("network")) return "The connection dropped during the upload. Try again.";
  return reason[0]!.toUpperCase() + reason.slice(1);
}

/** Whether to keep asking the backend: anything not final. */
export const needsPolling = (states: UploadState[]) => states.some((s) => !s.final);
